import { atom, useAtomValue } from 'jotai'
import type { Atom } from 'jotai'
import { useCallback } from 'react'
import type { ReserveChildMenuInput } from '~/lib/graphql/graphql'
import type { ChildMenuId } from '~/lib/graphql/scalars'
import { useBack } from '~/navigation'
import { childCount$, selectedChildMenus$, visitorsCount$ } from './stateMachine/context'
import type { ReserveInputChildMenus } from './stateMachine/state'
import { childMenus$, useTransition } from './stateMachine/state'

export const adultCount$ = atom((get) => {
  return get(visitorsCount$) - get(childCount$)
})
adultCount$.debugLabel = 'adultCount$'

export function useAdultCount() {
  return useAtomValue(adultCount$)
}

export type SelectedChildMenuWithDetails = {
  childMenuId: ChildMenuId
  count: number
  price: number
}

export const childMenuAmount$: Atom<SelectedChildMenuWithDetails[] | []> = atom((get) => {
  const selectedChildMenus = get(selectedChildMenus$)
  const childMenus = get(childMenus$)
  if (childMenus.length === 0) {
    return []
  }
  return calcChildMenuAmount(childMenus, selectedChildMenus)
})
childMenuAmount$.debugLabel = 'childMenuAmount$'

export function useChildMenuAmount() {
  return useAtomValue(childMenuAmount$)
}

function calcChildMenuAmount(
  childMenus: ReserveInputChildMenus,
  selectedChildMenus: ReserveChildMenuInput[]
): SelectedChildMenuWithDetails[] {
  return selectedChildMenus.map((selectedChildMenu) => {
    const childMenu = childMenus.find(
      (childMenu) => childMenu.childMenuId === selectedChildMenu.childMenuId
    )
    return {
      childMenuId: selectedChildMenu.childMenuId,
      count: selectedChildMenu.count,
      price: childMenu?.price ?? 0,
    }
  })
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('calcChildMenuAmount', () => {
    expect(
      calcChildMenuAmount(
        [
          { childMenuId: '1' as ChildMenuId, price: 100 },
          { childMenuId: '2' as ChildMenuId, price: 200 },
          { childMenuId: '3' as ChildMenuId, price: 200 },
        ] as ReserveInputChildMenus,
        [
          { childMenuId: '1' as ChildMenuId, count: 1 },
          { childMenuId: '2' as ChildMenuId, count: 2 },
        ]
      )
    ).toEqual([
      { childMenuId: '1', count: 1, price: 100 },
      { childMenuId: '2', count: 2, price: 200 },
    ])
  })
}

export function useSetChildMenu() {
  const send = useTransition()
  const back = useBack()
  return useCallback(
    (childCount: number, childMenus: ReserveChildMenuInput[]) => {
      send({
        type: 'updated',
        childCount,
        childMenus,
      })
      back()
    },
    [send, back]
  )
}
